import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SeoHead, { LOCAL_BUSINESS_SCHEMA } from "@/components/public/SeoHead";
import { useReviewStats } from "@/hooks/useReviewStats";
import Breadcrumb from "@/components/public/Breadcrumb";
import NotFoundPage from "@/pages/public/NotFoundPage";
import FadeIn from "@/components/public/FadeIn";
import CTASection from "@/components/public/CTASection";
import ReviewCard from "@/components/public/ReviewCard";
import DevisForm from "@/components/public/DevisForm";
import PriceCalculator from "@/components/public/PriceCalculator";
import { Loader2, MapPin, BookOpen, ChevronDown, Eye, Heart, Sparkles, Wrench, Zap, Calculator } from "lucide-react";

const SERVICE_IMAGES = {
  "nettoyage-canape": {
    beforeAfter: { src: "/images/services/canape-avant-apres.jpeg", alt: "Avant et après nettoyage de canapé par injection-extraction à domicile en Suisse romande" },
    result: { src: "/images/services/canape-resultat.jpeg", alt: "Canapé propre après nettoyage professionnel Nettoyage Expert – tissu ravivé sans résidu" },
  },
  "nettoyage-matelas": {
    beforeAfter: { src: "/images/services/matelas-avant-apres.jpeg", alt: "Avant et après nettoyage de matelas – élimination des taches et acariens" },
    result: { src: "/images/services/matelas-resultat.jpeg", alt: "Matelas assaini après traitement anti-acariens professionnel Nettoyage Expert" },
  },
  "nettoyage-moquette": {
    beforeAfter: { src: "/images/services/moquette-avant-apres.jpeg", alt: "Avant et après nettoyage de moquette – fibres décrassées en profondeur" },
    result: { src: "/images/services/moquette-resultat.jpeg", alt: "Moquette propre après nettoyage par injection-extraction professionnelle" },
  },
  "nettoyage-terrasse": {
    beforeAfter: { src: "/images/services/terrasse-avant-apres.jpeg", alt: "Avant et après nettoyage de terrasse – mousse et algues éliminées" },
    result: { src: "/images/services/terrasse-resultat.jpeg", alt: "Terrasse remise à neuf après nettoyage haute pression et traitement anti-mousse" },
  },
  "nettoyage-facade": {
    beforeAfter: { src: "/images/services/facade-avant-apres.jpeg", alt: "Avant et après nettoyage de façade – traces noires et pollution éliminées" },
    result: { src: "/images/services/facade-resultat.jpeg", alt: "Façade propre après nettoyage professionnel et traitement hydrofuge" },
  },
  "nettoyage-toiture": {
    beforeAfter: { src: "/images/services/toiture-avant-apres.jpeg", alt: "Avant et après nettoyage de toiture – mousse et lichen éliminés des tuiles" },
    result: { src: "/images/services/toiture-resultat.jpeg", alt: "Toiture propre après démoussage professionnel et traitement longue durée" },
  },
  "nettoyage-panneaux-solaires": {
    beforeAfter: { src: "/images/services/panneaux-solaires-avant-apres.jpeg", alt: "Avant et après nettoyage de panneaux solaires – rendement optimisé" },
    result: { src: "/images/services/panneaux-solaires-resultat.jpeg", alt: "Panneaux solaires propres après nettoyage à l'eau osmosée – gain de rendement immédiat" },
  },
  "nettoyage-fin-chantier": {
    beforeAfter: { src: "/images/services/fin-chantier-avant-apres.jpeg", alt: "Avant et après nettoyage fin de chantier – poussière de plâtre et résidus éliminés" },
    result: { src: "/images/services/fin-chantier-resultat.jpeg", alt: "Logement prêt à habiter après nettoyage fin de chantier professionnel" },
  },
  "nettoyage-sieges-cinema": {
    beforeAfter: { src: "/images/services/sieges-cinema-avant-apres.jpeg", alt: "Avant et après nettoyage de sièges de cinéma – taches de boissons et popcorn éliminées" },
    result: { src: "/images/services/sieges-cinema-resultat.jpeg", alt: "Sièges de cinéma propres après nettoyage par injection-extraction nocturne" },
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC_SLUGS = ["nettoyage-canape","nettoyage-chaises","nettoyage-fauteuils","nettoyage-matelas","nettoyage-moquette","nettoyage-tapis","nettoyage-terrasse","nettoyage-vitres"];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [avis, setAvis] = useState([]);
  const [villes, setVilles] = useState([]);
  const [blogArticles, setBlogArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const reviewStats = useReviewStats();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [s, a, v] = await Promise.all([
          axios.get(`${API}/services/${slug}`),
          axios.get(`${API}/avis`),
          axios.get(`${API}/villes`),
        ]);
        setService(s.data);
        setAvis(a.data.filter(av => av.service === s.data.name));
        setVilles(v.data);
        try {
          const b = await axios.get(`${API}/blog?service=${encodeURIComponent(s.data.name)}&limit=6`);
          setBlogArticles(b.data.articles || []);
        } catch { setBlogArticles([]); }
      } catch { /* service not found */ }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="service-detail-loading">
        <div className="bg-[#336DBF] py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-8 w-64 bg-white/20 rounded animate-pulse mb-4" />
            <div className="h-12 w-96 bg-white/20 rounded animate-pulse mb-4" />
            <div className="h-5 w-80 bg-white/15 rounded animate-pulse" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-16 space-y-6">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!service) return <NotFoundPage />;

  const c = service.copywriting || {};
  const faq = c.faq || [];
  const showB2B = service.type_client !== "B2C" && service.bloc_b2b;
  const showB2C = service.type_client !== "B2B" && service.bloc_b2c;
  const images = SERVICE_IMAGES[slug];
  const priceNum = service.prix_moyen?.match(/\d+/)?.[0] || "";

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "provider": { "@type": "LocalBusiness", "@id": "https://www.nettoyage-expert.ch/#business" },
    "areaServed": { "@type": "Place", "name": "Suisse romande" },
    "description": `${service.name} professionnel en Suisse romande par Nettoyage Expert, noté 5 étoiles sur Google.`,
    ...(priceNum && {
      "offers": {
        "@type": "Offer",
        "priceCurrency": "CHF",
        "price": priceNum,
        "priceSpecification": { "@type": "UnitPriceSpecification", "price": priceNum, "priceCurrency": "CHF" }
      }
    })
  };

  const schemas = [serviceSchema];

  if (faq.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faq.map(f => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.réponse || f.reponse || ""
        }
      }))
    });
  }

  const breadcrumbItems = [
    { name: "Accueil", url: "/" },
    { name: "Services", url: "/services" },
    { name: service.name }
  ];
  const titlePrice = priceNum ? ` dès ${priceNum} CHF` : "";
  const titleText = `${service.name}${titlePrice}`;
  const descText = `${service.name} professionnel en Suisse romande${titlePrice}. Résultat visible immédiatement. 5★ Google. Devis gratuit en 2h.`;

  return (
    <div data-testid="service-detail-page">
      <SeoHead
        title={titleText}
        description={descText}
        path={`/services/${slug}`}
        ogImage={images?.beforeAfter?.src}
        breadcrumbs={breadcrumbItems}
        schemas={schemas}
      />
      <Breadcrumb items={breadcrumbItems} />
      {/* HERO */}
      <section className="bg-[#336DBF] py-16 sm:py-20" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4">
              <span className="text-white/80 text-sm">{service.prix_moyen}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight" data-testid="service-h1">
              {service.name} professionnel en Suisse romande
            </h1>
            {c.hook && <p className="mt-4 text-lg sm:text-xl text-white/85 max-w-2xl">{c.hook}</p>}
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center justify-center px-8 py-4 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors text-base sm:text-lg"
              data-testid="service-hero-cta"
            >
              Devis gratuit
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ANSWER-FIRST — SEO pour LLMs et moteurs */}
      <section className="py-10 sm:py-14 bg-white" data-testid="service-answer-first">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#062133]/70 text-base sm:text-lg leading-relaxed">
            Nettoyage Expert est la référence pour le {service.name.toLowerCase()} en Suisse romande. Basée à Yverdon-les-Bains et intervenant dans toute la Romandie (Lausanne, Genève, Fribourg, Neuchâtel, Sion, Montreux, Nyon), l'entreprise est notée 5 étoiles sur Google avec {reviewStats.count} avis vérifiés. Nettoyage Expert propose un service professionnel avec résultat visible immédiatement.
          </p>
        </div>
      </section>

      {/* PILIER 1 — PERTINENCE */}
      {c.pertinence && (
        <section className="py-16 sm:py-24 bg-white" data-testid="service-pertinence">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-[#336DBF]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF]">Pertinence</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Vous reconnaissez cette situation ?
              </h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{c.pertinence}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* PILIER 2 — ÉMOTION */}
      {c.emotion && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-emotion">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-6 h-6 text-[#336DBF]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF]">A savoir</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Ce que personne ne vous dit
              </h2>
              <p className="text-[#062133] text-lg leading-relaxed">{c.emotion}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* IMAGE AVANT/APRÈS */}
      {images?.beforeAfter && (
        <section className="py-10 sm:py-16 bg-white" data-testid="service-img-before-after">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="overflow-hidden rounded-xl shadow-lg">
                <img src={images.beforeAfter.src} alt={images.beforeAfter.alt} loading="lazy" className="w-full h-auto object-cover" />
              </div>
              <p className="mt-3 text-center text-sm text-[#062133]/50">Avant / Après — {service.name} par Nettoyage Expert</p>
            </FadeIn>
          </div>
        </section>
      )}

      <CTASection title="Ne laissez pas le problème s'aggraver" subtitle="Devis gratuit et sans engagement — réponse sous 2 heures" />

      {/* PILIER 3 — DÉSIR */}
      {c.desir && (
        <section className="py-16 sm:py-24 bg-white" data-testid="service-desir">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-[#336DBF]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF]">Résultat</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Imaginez le résultat
              </h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{c.desir}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* IMAGE RÉSULTAT */}
      {images?.result && (
        <section className="py-10 sm:py-16 bg-[#F4F6F8]" data-testid="service-img-result">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="overflow-hidden rounded-xl shadow-lg">
                <img src={images.result.src} alt={images.result.alt} loading="lazy" className="w-full h-auto object-cover" />
              </div>
              <p className="mt-3 text-center text-sm text-[#062133]/50">Résultat professionnel — {service.name}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* PILIER 4 — MÉTHODE */}
      {c.methode && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-methode">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <Wrench className="w-6 h-6 text-[#336DBF]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF]">Méthode</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Notre méthode professionnelle
              </h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{c.methode}</p>
            </FadeIn>
          </div>
        </section>
      )}

      <CTASection title="Prêt à passer à l'action ?" subtitle="Intervention rapide dans toute la Suisse romande" />

      {/* Bloc B2B */}
      {showB2B && (
        <section className="py-16 sm:py-24 bg-white" data-testid="service-b2b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Entreprises</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">Pour les professionnels</h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{service.bloc_b2b}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Bloc B2C */}
      {showB2C && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-b2c">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Particuliers</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">Pour les particuliers</h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{service.bloc_b2c}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Prix — Calculateur intégré ou CTA */}
      <section className="py-12 sm:py-16 bg-white" data-testid="service-prix">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            {CALC_SLUGS.includes(slug) ? (
              <PriceCalculator preselectedSlug={slug} />
            ) : (
              <div className="bg-[#336DBF]/5 border border-[#336DBF]/15 rounded-lg p-6 sm:p-8 text-center">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-3">Demandez votre devis gratuit</h2>
                <p className="text-[#062133]/60 mb-4">
                  Fréquence recommandée : {service.frequence === "ponctuel" ? "intervention ponctuelle" :
                    service.frequence === "recurrent" ? "entretien régulier (contrat)" :
                    "ponctuel ou en contrat régulier"}
                </p>
                <p className="text-sm text-[#062133]/60 mb-6">Devis gratuit et sans engagement — prix exact après évaluation</p>
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors" data-testid="service-calc-cta">
                  <Calculator size={18} className="mr-2" /> Demander un devis
                </Link>
              </div>
            )}
          </FadeIn>
        </div>
      </section>

      {/* PILIER 5 — ACTION */}
      {c.action && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-action">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-[#336DBF]" />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF]">Action</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Passez à l'action maintenant
              </h2>
              <p className="text-[#062133] text-lg leading-relaxed font-medium">{c.action}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Avis clients */}
      {avis.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="service-avis">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Temoignages</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">
                Ce que nos clients disent de ce service
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {avis.slice(0, 6).map((a, i) => (
                <FadeIn key={a.id || i} delay={i * 100}><ReviewCard avis={a} /></FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">FAQ</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">
                Questions fréquentes
              </h2>
            </FadeIn>
            <div className="space-y-3">
              {faq.map((item, i) => (
                <FadeIn key={i} delay={i * 50}>
                  <details className="group bg-white border border-[#062133]/10 rounded-lg" data-testid={`faq-${i}`}>
                    <summary className="flex items-center justify-between cursor-pointer p-5 list-none [&::-webkit-details-marker]:hidden">
                      <span className="font-medium text-[#062133] pr-4">{item.question}</span>
                      <ChevronDown className="w-5 h-5 text-[#062133]/60 shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-[#062133]/60 leading-relaxed">{item.réponse || item.reponse}</p>
                    </div>
                  </details>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* City Cross-links */}
      {villes.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="service-city-links">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Proximité</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">Ce service dans votre ville</h2>
            </FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {villes.map((v, i) => (
                <FadeIn key={v.slug} delay={i * 50}>
                  <Link to={`/${slug}-${v.slug}`} className="block bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-4 hover:border-[#336DBF]/30 hover:shadow-sm transition-all" data-testid={`city-link-${v.slug}`}>
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#336DBF]" />
                      <span className="text-sm font-medium text-[#062133]">{v.name}</span>
                    </div>
                    <span className="text-xs text-[#062133]/60 mt-1 block">{v.canton}</span>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog articles for this service */}
      {blogArticles.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="service-blog-articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Blog</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">Articles sur ce service</h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {blogArticles.map((a, i) => (
                <FadeIn key={a.slug} delay={i * 50}>
                  <Link to={`/blog/${a.slug}`} className="block bg-white border border-[#062133]/10 rounded-lg p-4 hover:border-[#336DBF]/30 hover:shadow-sm transition-all">
                    <div className="flex items-start gap-3">
                      <BookOpen size={16} className="text-[#336DBF] shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-[#062133] line-clamp-2">{a.title}</h3>
                        <span className="text-xs text-[#062133]/60 mt-1 block">{a.ville_name}</span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DevisForm */}
      <section className="py-16 sm:py-24 bg-white" id="devis-section" data-testid="devis-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Contact</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">
                Demandez votre devis pour {service.name}
              </h2>
              <p className="mt-2 text-[#062133]/60">Réponse garantie sous 2 heures</p>
            </div>
            <div className="bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-6 sm:p-8">
              <DevisForm source="service" prefilledService={service.name} />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
